import { replaceEqualDeep } from '@tanstack/react-query';

const hasRevision = (
  value: unknown
): value is { id: number; tenantId: string; updatedAt: string } =>
  typeof value === 'object' &&
  value !== null &&
  'id' in value &&
  typeof value.id === 'number' &&
  'tenantId' in value &&
  typeof value.tenantId === 'string' &&
  'updatedAt' in value &&
  typeof value.updatedAt === 'string' &&
  Number.isFinite(Date.parse(value.updatedAt));

/**
 * Keeps a later canonical conversation revision when a detail GET lags behind.
 * Equal revisions may be replaced by a fresh authoritative read; pre-event reads
 * are separately cancelled at the socket boundary. This never merges tenants.
 * @param previous - Current detail cache entry.
 * @param incoming - HTTP response or accepted canonical snapshot.
 * @returns The newest known revision with ordinary structural sharing otherwise.
 */
export const reconcileConversationSnapshot = <T>(
  previous: T | undefined,
  incoming: T
): T => {
  if (
    hasRevision(previous) &&
    hasRevision(incoming) &&
    previous.id === incoming.id &&
    previous.tenantId === incoming.tenantId &&
    Date.parse(previous.updatedAt) > Date.parse(incoming.updatedAt)
  ) {
    return previous;
  }
  return replaceEqualDeep(previous, incoming);
};
